/* luxe-customizer.js
   Paste this entire file into: Assets → luxe-customizer.js

   REQUIRED:
   1) Replace CLOUD_NAME + UPLOAD_PRESET below
   2) Your form/snippet must include these inputs somewhere inside [data-luxe-customizer]:
      - <input type="file" data-luxe-photo-input accept="image/*">
      - <input type="hidden" name="properties[Customer Photo]" data-luxe-photo-url>
      - <input type="hidden" name="properties[Try-on Preview]" data-luxe-tryon-url>
      - <input type="hidden" data-luxe-overlay-url value="{{ 'overlay.png' | asset_url }}">
   Optional buttons/elems:
      - data-luxe-open-camera, data-luxe-upload-photo, data-luxe-save-preview, data-luxe-clear
      - data-luxe-tryon-input (file input)
      - data-luxe-video (video), data-luxe-canvas (canvas), data-luxe-status (status text), data-luxe-help (help text)
*/

(function () {
  // =========================
  // CONFIG (Cloudinary)
  // =========================
  // Replace these two values:
  const CLOUD_NAME = "YOUR_CLOUD_NAME";
  const UPLOAD_PRESET = "YOUR_UNSIGNED_UPLOAD_PRESET";
  const MAX_MB = 10;

  // =========================
  // Cloudinary upload
  // =========================
  async function uploadToCloudinary(fileOrBlob) {
    if (!CLOUD_NAME || CLOUD_NAME === "YOUR_CLOUD_NAME") {
      throw new Error("Set CLOUD_NAME in luxe-customizer.js");
    }
    if (!UPLOAD_PRESET || UPLOAD_PRESET === "YOUR_UNSIGNED_UPLOAD_PRESET") {
      throw new Error("Set UPLOAD_PRESET in luxe-customizer.js");
    }

    const formData = new FormData();
    formData.append("file", fileOrBlob);
    formData.append("upload_preset", UPLOAD_PRESET);

    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Cloudinary upload failed");
    }

    const data = await res.json();
    if (!data.secure_url) throw new Error("Cloudinary did not return a secure_url");
    return data.secure_url;
  }

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  function fileToImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  async function dataUrlToBlob(dataUrl) {
    const res = await fetch(dataUrl);
    return await res.blob();
  }

  function safeSetText(el, txt) {
    if (!el) return;
    el.textContent = txt || "";
  }

  function initOne(root) {
    // Required elements
    const photoInput = root.querySelector("[data-luxe-photo-input]");
    const photoUrlInput = root.querySelector("[data-luxe-photo-url]");
    const statusEl = root.querySelector("[data-luxe-status]");
    const video = root.querySelector("[data-luxe-video]");
    const canvas = root.querySelector("[data-luxe-canvas]");

    // Optional elements
    const help = root.querySelector("[data-luxe-help]");
    const btnOpenCamera = root.querySelector("[data-luxe-open-camera]");
    const btnUploadPhoto = root.querySelector("[data-luxe-upload-photo]");
    const btnSavePreview = root.querySelector("[data-luxe-save-preview]");
    const btnClear = root.querySelector("[data-luxe-clear]");
    const tryonInput = root.querySelector("[data-luxe-tryon-input]");
    const tryonUrlInput = root.querySelector("[data-luxe-tryon-url]");

    // Overlay URL from your Liquid hidden input
    const overlayUrl = root.querySelector("[data-luxe-overlay-url]")?.value || "";

    // If your markup isn’t on this page, exit quietly
    if (!photoInput || !photoUrlInput || !video || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Make sure canvas has a size (if your markup doesn’t set it)
    if (!canvas.width) canvas.width = 800;
    if (!canvas.height) canvas.height = 1000;

    const overlayImg = new Image();
    overlayImg.crossOrigin = "anonymous";
    if (overlayUrl) overlayImg.src = overlayUrl;

    const state = {
      photo: null,
      overlay: overlayImg,
      overlayX: 220,
      overlayY: 340,
      overlayScale: 1.0,
      dragging: false,
      lastX: 0,
      lastY: 0,
      stream: null,
      pinch: { active: false, startDist: 0, startScale: 1 },
    };

    function setStatus(msg) {
      safeSetText(statusEl, msg);
    }

    function draw() {
      if (!state.photo) return;

      const cw = canvas.width,
        ch = canvas.height;
      ctx.clearRect(0, 0, cw, ch);

      // Cover-fit customer photo
      const iw = state.photo.width,
        ih = state.photo.height;
      const s = Math.max(cw / iw, ch / ih);
      const pw = iw * s,
        ph = ih * s;
      const px = (cw - pw) / 2,
        py = (ch - ph) / 2;
      ctx.drawImage(state.photo, px, py, pw, ph);

      // Overlay product PNG
      if (state.overlay && state.overlay.complete && state.overlay.naturalWidth) {
        const ow = state.overlay.naturalWidth * state.overlayScale;
        const oh = state.overlay.naturalHeight * state.overlayScale;
        ctx.drawImage(state.overlay, state.overlayX, state.overlayY, ow, oh);
      }

      safeSetText(help, "Drag the overlay to position it. Mouse wheel (or pinch) to resize.");
    }

    async function uploadCustomerFile(file) {
      if (file.size > MAX_MB * 1024 * 1024) {
        throw new Error(`Please choose an image under ${MAX_MB}MB.`);
      }

      setStatus("Uploading photo…");
      photoUrlInput.value = "";

      const url = await uploadToCloudinary(file);
      photoUrlInput.value = url;

      setStatus("Photo uploaded ✓");
      return url;
    }

    async function uploadCanvasPreview() {
      if (!state.photo) return;
      if (!tryonUrlInput) {
        setStatus("Missing try-on URL input (data-luxe-tryon-url).");
        return;
      }

      setStatus("Uploading try-on preview…");
      tryonUrlInput.value = "";

      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      const blob = await dataUrlToBlob(dataUrl);
      const url = await uploadToCloudinary(blob);

      tryonUrlInput.value = url;
      setStatus("Try-on preview uploaded ✓");
    }

    // Main photo upload (uploads + loads into canvas)
    photoInput.addEventListener("change", async () => {
      const file = photoInput.files && photoInput.files[0];
      if (!file) return;

      try {
        await uploadCustomerFile(file);
        state.photo = await fileToImage(file);

        canvas.style.display = "block";
        video.style.display = "none";
        draw();
      } catch (e) {
        console.error(e);
        setStatus(e?.message || "Upload failed. Please try again.");
        photoInput.value = "";
      }
    });

    // Try-on upload (loads into canvas, does NOT upload until Save Preview)
    if (btnUploadPhoto && tryonInput) {
      btnUploadPhoto.addEventListener("click", () => tryonInput.click());
    }
    if (tryonInput) {
      tryonInput.addEventListener("change", async () => {
        const file = tryonInput.files && tryonInput.files[0];
        if (!file) return;

        try {
          state.photo = await fileToImage(file);
          canvas.style.display = "block";
          video.style.display = "none";
          draw();
          setStatus("Photo loaded. Position overlay, then Save Preview.");
        } catch (e) {
          console.error(e);
          setStatus("Couldn’t load that image. Try another.");
          tryonInput.value = "";
        }
      });
    }

    // Camera
    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported.");

      if (state.stream) {
        state.stream.getTracks().forEach((t) => t.stop());
        state.stream = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      state.stream = stream;
      video.srcObject = stream;
      video.style.display = "block";
      canvas.style.display = "none";
      safeSetText(help, "Camera is on. Click the video to capture.");

      video.onclick = () => {
        const temp = document.createElement("canvas");
        temp.width = video.videoWidth || 800;
        temp.height = video.videoHeight || 1000;
        temp.getContext("2d").drawImage(video, 0, 0);

        const img = new Image();
        img.onload = () => {
          state.photo = img;
          video.style.display = "none";
          canvas.style.display = "block";
          draw();

          if (state.stream) {
            state.stream.getTracks().forEach((t) => t.stop());
            state.stream = null;
            video.srcObject = null;
          }
        };
        img.src = temp.toDataURL("image/jpeg", 0.92);
      };
    }

    if (btnOpenCamera) {
      btnOpenCamera.addEventListener("click", async () => {
        try {
          await startCamera();
        } catch (e) {
          console.error(e);
          safeSetText(help, "Couldn’t access camera. Please upload a photo instead.");
        }
      });
    }

    if (btnSavePreview) {
      btnSavePreview.addEventListener("click", async () => {
        try {
          await uploadCanvasPreview();
        } catch (e) {
          console.error(e);
          setStatus("Preview upload failed. Try again.");
        }
      });
    }

    // Clear
    if (btnClear) {
      btnClear.addEventListener("click", () => {
        if (state.stream) {
          state.stream.getTracks().forEach((t) => t.stop());
          state.stream = null;
          video.srcObject = null;
        }

        state.photo = null;
        state.overlayX = 220;
        state.overlayY = 340;
        state.overlayScale = 1.0;

        canvas.style.display = "none";
        video.style.display = "none";

        if (tryonInput) tryonInput.value = "";
        photoInput.value = "";

        photoUrlInput.value = "";
        if (tryonUrlInput) tryonUrlInput.value = "";

        setStatus("");
        safeSetText(help, "Tip: Drag the overlay to position it. Mouse wheel (or pinch) to resize.");
      });
    }

    // Overlay interactions (mouse)
    canvas.addEventListener("mousedown", (e) => {
      state.dragging = true;
      state.lastX = e.offsetX;
      state.lastY = e.offsetY;
    });

    window.addEventListener("mouseup", () => (state.dragging = false));

    canvas.addEventListener("mousemove", (e) => {
      if (!state.dragging) return;
      const dx = e.offsetX - state.lastX;
      const dy = e.offsetY - state.lastY;
      state.overlayX += dx;
      state.overlayY += dy;
      state.lastX = e.offsetX;
      state.lastY = e.offsetY;
      draw();
    });

    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const delta = Math.sign(e.deltaY);
        state.overlayScale *= delta > 0 ? 0.95 : 1.05;
        state.overlayScale = clamp(state.overlayScale, 0.2, 3.0);
        draw();
      },
      { passive: false }
    );

    // Touch drag + pinch
    canvas.addEventListener(
      "touchstart",
      (e) => {
        if (!state.photo) return;

        if (e.touches.length === 1) {
          const rect = canvas.getBoundingClientRect();
          const x = (e.touches[0].clientX - rect.left) * (canvas.width / rect.width);
          const y = (e.touches[0].clientY - rect.top) * (canvas.height / rect.height);
          state.dragging = true;
          state.lastX = x;
          state.lastY = y;
        } else if (e.touches.length === 2) {
          state.pinch.active = true;
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          state.pinch.startDist = Math.hypot(dx, dy);
          state.pinch.startScale = state.overlayScale;
        }
      },
      { passive: true }
    );

    canvas.addEventListener(
      "touchmove",
      (e) => {
        if (!state.photo) return;

        if (e.touches.length === 1 && state.dragging && !state.pinch.active) {
          const rect = canvas.getBoundingClientRect();
          const x = (e.touches[0].clientX - rect.left) * (canvas.width / rect.width);
          const y = (e.touches[0].clientY - rect.top) * (canvas.height / rect.height);
          const dx = x - state.lastX;
          const dy = y - state.lastY;
          state.overlayX += dx;
          state.overlayY += dy;
          state.lastX = x;
          state.lastY = y;
          draw();
        } else if (e.touches.length === 2 && state.pinch.active) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const dist = Math.hypot(dx, dy);
          const ratio = dist / (state.pinch.startDist || dist);
          state.overlayScale = clamp(state.pinch.startScale * ratio, 0.2, 3.0);
          draw();
        }
      },
      { passive: true }
    );

    canvas.addEventListener(
      "touchend",
      (e) => {
        state.dragging = false;
        if (e.touches.length < 2) state.pinch.active = false;
      },
      { passive: true }
    );

    overlayImg.onload = () => {
      if (state.photo) draw();
    };

    // Tiny “sanity” message so it doesn’t feel like nothing happened
    setStatus("");
    safeSetText(help, "Upload a photo to start, then drag/resize the overlay.");
  }

  function initAll() {
    document.querySelectorAll("[data-luxe-customizer]").forEach((root) => {
      if (root.__luxeInit) return;
      root.__luxeInit = true;
      initOne(root);
    });
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();
